import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AfterViewInit, Component, effect, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColDef,
  ColumnApiModule,
  ColumnAutoSizeModule,
  EventApiModule,
  GridApi,
  GridOptions,
  ModuleRegistry,
  PaginationModule,
  QuickFilterModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowSelectionModule,
  RowSelectionOptions,
  SelectionColumnDef,
  TextFilterModule,
  ValidationModule,
} from 'ag-grid-community';
import { TeamMember } from '../app.models';
import { AppService } from '../app.service';
import { ApplicationInsightsService } from '../application-insights.service';
import { TemplateRendererComponent } from '../common/ag-grid-components/template-renderer.component';
import { SwooshComponent } from '../common/ui-elements/swoosh.component';
import { IntroductionService } from '../services/introduction.service';
import { SafeHtmlPipe } from '../templates/safe.pipe';
import { HeaderCheckboxCellRendererComponent } from './header-checkbox-cell-renderer/header-checkbox-cell-renderer.component';
import { ManageTeamService } from './services/manage-team/manage-team.service';
import { columnDefinitions } from './types';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ValidationModule,
  PaginationModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  CellStyleModule,
  ClientSideRowModelApiModule,
  RowSelectionModule,
  RowApiModule,
  EventApiModule,
  RenderApiModule,
  QuickFilterModule,
  TextFilterModule,
  ColumnApiModule,
]);

@Component({
  selector: 'app-manage',
  templateUrl: './manage.component.html',
  styleUrls: ['./manage.component.scss'],
  standalone: true,
  imports: [AgGridAngular, SwooshComponent, SafeHtmlPipe, CommonModule, FormsModule, ReactiveFormsModule],
  providers: [NgbModal],
})
export class ManageComponent implements OnInit, AfterViewInit {
  @ViewChild('agGrid') agGrid: AgGridAngular;

  // @HostListener('window:resize', ['$event'])
  // onResize() {
  //   this._handleResize();
  // }

  private resizeTimeout: any;

  @HostListener('window:resize', [])
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this._handleResize();
    }, 200); // 200ms delay — adjust as needed
  }

  public HttpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  swooshText = 'Back to Assessment';
  swooshExternal = '';
  isMobile: boolean = false;
  introduction: string = '';
  noneSelected: boolean = true;
  reminderResponse: string = '';
  reminderSent: boolean = false;
  hideReminderSpinner: boolean = true;

  gridApi: GridApi;
  gridColumnApi: any;
  rowData: unknown[];
  completed: number = 0;

  title: string;

  defaultColDef: ColDef;
  numPages: number = 0;
  paginationPageSize: number = 10;
  arrowFirst: string = 'inactive-arrow';
  arrowPrevious: string = 'inactive-arrow';
  arrowNext: string = 'active-arrow';
  arrowLast: string = 'active-arrow';

  lbTotalPages: number = 1;
  displayStart: number = 1;
  displayEnd: number = 1;
  numTeamMembers: number = 1;

  /**
   * Additional/New AG-Grid features.
   */
  columDefinitions: ColDef[] = columnDefinitions.columnsDesktop;

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: false,
    headerCheckbox: false,
  };

  selectionColumnDef: SelectionColumnDef = {
    cellRenderer: HeaderCheckboxCellRendererComponent,
    pinned: false,
  };

  gridOptions: GridOptions = {
    suppressCellFocus: true,
  };

  frameworkComponents = {
    templateRendererComponent: TemplateRendererComponent,
  };

  constructor(
    private appService: AppService,
    private introductionService: IntroductionService,
    private modalService: NgbModal,
    private applicationInsightsService: ApplicationInsightsService,
    private http: HttpClient,

    private router: Router,
    private _manageTeamService: ManageTeamService,
  ) {
    this.defaultColDef = {
      flex: 1,
      autoHeight: true,
      sortable: true,
      resizable: true,
    };

    effect(() => {
      if (this._manageTeamService.triggerGridRefresh()) {
        this.populateGrid();
      }
    });

    if (window.innerWidth < 768) {
      this.columDefinitions = columnDefinitions.columnsMobile;
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  ngAfterViewInit(): void {
    this._handleResize();
  }

  onRowSelected = () => {
    this.gridApi.refreshCells({ force: true });
  };

  ngOnInit() {
    if (!this.appService.getCurrentTeamMember().isTeamLeader) {
      this.router.navigate(['/team']);
    }
    if (!this.appService.getCurrentTeamMember().mbtiType) {
      this.appService.getUserDetails().subscribe(
        (data) => {
          if (this.appService.getTeamInfo().isElevateTeam) {
            this.swooshText = 'Back to Your Elevate Dashboard';
            this.swooshExternal = this.appService.getTeamInfo().redirectUrl;
          } else {
            this.swooshExternal = data.assessmentSiteBaseUrl + '/assessment';
          }
        },
        (err) => this.applicationInsightsService.logException(err),
      );
    } else {
      if (this.appService.getTeamInfo().isElevateTeam) {
        this.swooshText = 'Back to Your Elevate Dashboard';
        this.swooshExternal = this.appService.getTeamInfo().redirectUrl;
      } else {
        this.swooshText = '';
      }
    }
    this.appService.isElevateUser$.subscribe((isElevateUser) => {
      if (isElevateUser !== null) {
        if (isElevateUser) {
          this.introduction = this.introductionService.getContent('ManageTeams');
        } else {
          this.introduction = this.introductionService.getContent('ManageTeams-B2C');
        }
      }
    });

    this.populateGrid();
  }

  populateGrid() {
    this.completed = 0;
    this.rowData = [];

    this.appService.getTeamMembers().forEach((tm: TeamMember, index) => {
      this.rowData.push({
        id: index,
        teamMemberId: tm.teamMemberId,
        firstName: tm.firstName,
        lastName: tm.lastName,
        fullName: tm.firstName + ' ' + tm.lastName,
        emailAddress: tm.emailAddress,
        status: tm.status,
        accessCode: tm.giftCode,
        selected: false,
        isTeamLeader: tm.isTeamLeader,
        inviteDate: tm.inviteDate,
        inviteLink: `${this.appService.team.linkBackUrl}&userId=${tm.personId}`,
      });

      if (tm.status === 'Completed') this.completed++;
    });
  }

  /**
   * @description - Handles the window resize event to adjust the grid's column definitions based on the screen size.
   * It sets the column definitions to mobile or desktop versions and resets the grid state accordingly.
   */
  private _handleResize() {
    if (!this.gridApi) {
      return;
    }

    if (window.innerWidth < 768) {
      this.gridApi.setGridOption('columnDefs', columnDefinitions.columnsMobile);
      this.isMobile = true;
    } else {
      this.gridApi.setGridOption('columnDefs', columnDefinitions.columnsDesktop);
      this.gridApi.resetRowHeights();
      this.isMobile = false;
    }

    // Reset the grid state after the column definitions are swapped.
    this.gridApi.resetColumnState();
    this.gridApi.resetRowHeights();
    this.gridApi.redrawRows();
    this.gridApi.sizeColumnsToFit();
  }

  get disableSendReminderButton() {
    if (this.gridApi) {
      if (this.gridApi.getSelectedRows().length > 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  showReminderModal(content) {
    this.modalService.open(content, { centered: true, windowClass: 'modal650px' });
    this.reminderSent = false;
  }

  sendReminder() {
    let emailAddresses = [];

    const teamMemberArray = this.gridApi.getSelectedRows(); // TODO: Should this be all the rowData or just the selected ones? Changed it to selected ones for now.
    teamMemberArray.forEach((teamMember) => {
      if (teamMember.emailAddress) {
        emailAddresses.push(teamMember.emailAddress);
      }
    });
    this.reminderSent = true;
    this.reminderResponse = '';

    if (emailAddresses.length) {
      let reminder = emailAddresses.length === 1 ? 'reminder' : 'reminders';
      let verb = emailAddresses.length === 1 ? 'has been' : 'have been';
      this.hideReminderSpinner = false;

      this.http.post('/api/teams/remind', emailAddresses, this.HttpOptions).subscribe(
        () => {
          this.reminderResponse = `Your email ${reminder} ${verb} sent`;
          this.hideReminderSpinner = true;
        },
        (err) => {
          this.applicationInsightsService.logException(err);
          this.reminderResponse = 'We apologize, an error occurred.  Please try again';
          this.hideReminderSpinner = true;
        },
      );

      this.gridApi.forEachNode((node) => {
        node.setSelected(false, true);
      });
      this.gridApi.refreshCells({ force: true });
    } else this.reminderResponse = 'You have not selected any team members.  Please try again';
  }

  public changePagination(event): void {
    this.paginationPageSize = +event.target.value;
    // this.gridApi.paginationSetPageSize(this.paginationPageSize);
    this.gridApi.setGridOption('paginationPageSize', this.paginationPageSize);
    this.numPages = this.gridApi.paginationGetTotalPages();
    (<HTMLSelectElement>document.getElementById('goToPageDropdown')).selectedIndex = 0;
    this.gridApi.paginationGoToFirstPage();
    this.calculateArrowColor();
  }

  public goToPage(event): void {
    let page = +event.target.value;
    this.gridApi.paginationGoToPage(page - 1);
    this.calculateArrowColor();
  }

  calculateArrowColor() {
    if (this.gridApi.paginationGetTotalPages() === 1) {
      this.arrowFirst = 'inactive-arrow';
      this.arrowPrevious = 'inactive-arrow';
      this.arrowNext = 'inactive-arrow';
      this.arrowLast = 'inactive-arrow';
      return;
    }

    if (this.gridApi.paginationGetCurrentPage() === 0) {
      this.arrowFirst = 'inactive-arrow';
      this.arrowPrevious = 'inactive-arrow';
    } else {
      this.arrowFirst = 'active-arrow';
      this.arrowPrevious = 'active-arrow';
    }

    if (this.gridApi.paginationGetCurrentPage() === this.gridApi.paginationGetTotalPages() - 1) {
      this.arrowNext = 'inactive-arrow';
      this.arrowLast = 'inactive-arrow';
    } else {
      this.arrowNext = 'active-arrow';
      this.arrowLast = 'active-arrow';
    }
  }

  onPaginationChanged() {
    if (this.gridApi) {
      this.numPages = this.gridApi.paginationGetTotalPages();
      this.numTeamMembers = this.rowData.length;
      let rowsPerPage = this.gridApi.paginationGetPageSize();
      this.displayStart = this.gridApi.paginationGetCurrentPage() * rowsPerPage + 1;
      this.displayEnd = this.displayStart + rowsPerPage - 1;

      if (this.displayEnd > this.numTeamMembers) this.displayEnd = this.numTeamMembers;
    }
  }

  onBtFirst() {
    this.gridApi.paginationGoToFirstPage();
    (<HTMLSelectElement>document.getElementById('goToPageDropdown')).selectedIndex = this.gridApi.paginationGetCurrentPage();
    this.calculateArrowColor();
  }

  onBtPrevious() {
    this.gridApi.paginationGoToPreviousPage();
    (<HTMLSelectElement>document.getElementById('goToPageDropdown')).selectedIndex = this.gridApi.paginationGetCurrentPage();
    this.calculateArrowColor();
  }

  onBtNext() {
    this.gridApi.paginationGoToNextPage();
    (<HTMLSelectElement>document.getElementById('goToPageDropdown')).selectedIndex = this.gridApi.paginationGetCurrentPage();
    this.calculateArrowColor();
  }

  onBtLast() {
    this.gridApi.paginationGoToLastPage();
    (<HTMLSelectElement>document.getElementById('goToPageDropdown')).selectedIndex = this.gridApi.paginationGetCurrentPage();
    this.calculateArrowColor();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
    this.gridApi.setGridOption('rowData', this.rowData);
  }

  onTeamSearch(event) {
    this.gridApi.setGridOption('quickFilterText', event.target.value);
  }
}
